import { useChatStore } from "@/store/chat.store";
import type { ThreadDto } from "@/api/chat/dto/thread.dto";
import type { MessageDto } from "@/api/chat/dto/message.dto";
import { useCache } from "@/composables/useCache";
import { API } from "@/api/api";

export function useChatSocket() {
  const chatStore = useChatStore();
  const { upsertCachedThread } = useCache();
  const api = new API();

  const onMessage = (message: MessageDto) => {
    chatStore.updateThreadMessage(message);
  };

  const onMessages = (messages: MessageDto[]) => {
    chatStore.updateThreadMessage(messages);
  };

  const onThread = async (thread: ThreadDto) => {
    await upsertCachedThread(thread);
  };

  const onThreads = async (threads: ThreadDto[]) => {
    await Promise.all(threads.map((thread) => upsertCachedThread(thread)));
  };

  const onOnlineStatus = (isOnline: boolean) => {
    chatStore.setOffline(!isOnline);
  };

  const onRecovery = async (lastEvent: Date | null): Promise<boolean> => {
    const timeFrom = lastEvent ? Math.round(lastEvent.getTime() / 1000).toString() : null;

    try {
      const resp = await api.chat.getThreads(0, 1000, timeFrom);

      if (!resp.success || !resp.data) {
        return false;
      }

      for (const thread of resp.data.threads) {
        chatStore.upsertThread(thread);
      }

      chatStore.sortThreads();
      chatStore.recalcThreadsCount();
      return true;
    } catch (error) {
      console.error("Error recovering chat threads", error);
      return false;
    }
  };

  return { onMessage, onMessages, onThread, onThreads, onOnlineStatus, onRecovery };
}
